// priceTab/styled.js - Updated with dynamic pricing toggle styles
import { styled } from '@mui/material/styles';
import { Box, Card, Alert, Button, Paper } from '@mui/material';

// Import the Gallery header styles (assuming they're in a shared location or copied here)
export const HoneyContainer = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: '#5D4E37', // honeyBrown
  maxWidth: '800px',
  margin: '0 auto',
}));

export const HoneyGalleryHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

export const HoneyGalleryTitle = styled(Box)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  background: 'linear-gradient(135deg, #D4A574, #F4D03F)', // honeycombGradient
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

export const HoneyGallerySubtitle = styled(Box)(({ theme }) => ({
  color: '#8B7355', // lightBrown
  fontSize: '1.1rem',
  fontWeight: 400,
}));

// VAT Toggle Styling
export const StyledVATToggle = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFEF7', // softCream
  borderRadius: '16px',
  padding: theme.spacing(2),
  border: '2px solid #F4D03F30', // brightHoney with opacity
  marginBottom: theme.spacing(3),
  boxShadow: '0 4px 12px rgba(212, 165, 116, 0.15)', // honeycomb shadow
}));

// Dynamic Pricing Toggle Styling
export const StyledDynamicPricingToggle = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFEF7', // softCream
  borderRadius: '20px',
  padding: theme.spacing(2.5),
  border: '2px solid #D4A574', // honeycomb
  marginBottom: theme.spacing(2),
  boxShadow: '0 8px 24px rgba(212, 165, 116, 0.2)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    borderColor: '#F1C40F', // goldenHoney
    boxShadow: '0 12px 32px rgba(241, 196, 15, 0.25)',
  },
}));

// Dynamic Pricing Container (for the collapsible content)
export const StyledDynamicPricingContainer = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
}));

// Tariff Card Styling
export const StyledTariffCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#FFFEF7', // softCream
  borderRadius: '24px',
  border: '2px solid #F4D03F30', // brightHoney with opacity
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(93, 78, 55, 0.12)', // honeyBrown shadow
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    borderColor: '#F1C40F50', // goldenHoney with opacity
    boxShadow: '0 12px 48px rgba(93, 78, 55, 0.18)',
    transform: 'translateY(-2px)',
  },
}));

// Base Price Section
export const StyledBasePriceSection = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: theme.spacing(2.5),
  border: '1px solid #F4D03F30', // brightHoney with opacity
  marginBottom: theme.spacing(2),
}));

// Rule Box Styling
export const StyledRuleBox = styled(Paper)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: theme.spacing(2),
  border: '1px solid #D4A574', // honeycomb
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(212, 165, 116, 0.1)',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    borderColor: '#F1C40F', // goldenHoney
    boxShadow: '0 8px 24px rgba(241, 196, 15, 0.15)',
  },
}));

// New Rule Box Styling
export const StyledNewRuleBox = styled(Paper)(({ theme }) => ({
  backgroundColor: '#F1C40F10', // goldenHoney with low opacity
  borderRadius: '16px',
  padding: theme.spacing(2.5),
  border: '2px dashed #F1C40F', // goldenHoney
  marginTop: theme.spacing(2),
  position: 'relative',
  
  '&::before': {
    content: '"NEW"',
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: '#F1C40F', // goldenHoney
    color: '#5D4E37', // honeyBrown
    padding: '4px 8px',
    borderRadius: '8px',
    fontSize: '0.7rem',
    fontWeight: 700,
  },
}));

// Day Checkbox Group
export const StyledDayCheckboxGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  
  '& .MuiFormControlLabel-root': {
    margin: 0,
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: theme.spacing(0.5, 1),
    border: '1px solid #F4D03F50', // brightHoney with opacity
    transition: 'all 0.2s ease',
    
    '&:hover': {
      borderColor: '#D4A574', // honeycomb
      backgroundColor: '#F4D03F10', // brightHoney with low opacity
    },
  },
  
  '& .MuiCheckbox-root.Mui-checked': {
    color: '#F1C40F', // goldenHoney
  },
}));

// Action Buttons Container
export const StyledActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'flex-end',
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  borderTop: '1px solid #F4D03F30', // brightHoney with opacity
}));

// Empty State
export const StyledEmptyState = styled(Alert)(({ theme }) => ({
  borderRadius: '16px',
  backgroundColor: '#F4D03F10', // brightHoney with low opacity
  border: '1px solid #D4A574', // honeycomb
  color: '#5D4E37', // honeyBrown
}));

// Loading Container
export const StyledLoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  color: '#8B7355', // lightBrown
}));

// Error Alert
export const StyledErrorAlert = styled(Alert)(({ theme }) => ({
  borderRadius: '12px',
  marginBottom: theme.spacing(2),
}));

// Save Button
export const StyledSaveButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #F1C40F, #F4D03F)', // honeyGradient
  color: '#5D4E37', // honeyBrown
  borderRadius: '50px',
  padding: theme.spacing(1.5, 4),
  fontWeight: 700,
  fontSize: '1.1rem',
  boxShadow: '0 8px 32px rgba(241, 196, 15, 0.5)', // goldenHoney shadow
  transition: 'all 0.3s ease',
  
  '&:hover': {
    background: 'linear-gradient(135deg, #D4A574, #F1C40F)', // honeycombGradient
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 48px rgba(241, 196, 15, 0.7)',
  },
  
  '&:disabled': {
    opacity: 0.6,
    transform: 'none',
  },
}));

// Add Rule Button
export const StyledAddRuleButton = styled(Button)(({ theme }) => ({
  borderStyle: 'dashed',
  borderWidth: 2,
  borderColor: '#D4A574', // honeycomb
  color: '#D4A574', // honeycomb
  borderRadius: '50px',
  padding: theme.spacing(1, 3),
  fontWeight: 600,
  transition: 'all 0.3s ease',
  
  '&:hover': {
    backgroundColor: '#D4A57410', // honeycomb with low opacity
    borderColor: '#F1C40F', // goldenHoney
    color: '#F1C40F', // goldenHoney
  },
}));

// Tariff Action Button (for activate/deactivate)
export const StyledTariffActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: theme.spacing(1, 3),
  fontWeight: 600,
  transition: 'all 0.3s ease',
  
  '&.success-variant': {
    borderColor: '#4caf50',
    color: '#4caf50',
    
    '&:hover': {
      backgroundColor: '#4caf50',
      color: 'white',
    },
  },
  
  '&.error-variant': {
    borderColor: '#f44336',
    color: '#f44336',
    
    '&:hover': {
      backgroundColor: '#f44336',
      color: 'white',
    },
  },
}));